/**
 * Generic "capture a DOM element to PNG/PDF" helpers.
 *
 * Extracted from the Gantt exporter so any full-content surface (Gantt,
 * dashboard charts, …) can render itself to an image or PDF. Captures the
 * element at its full scroll size — un-clipping overflow and un-truncating
 * text in the clone — so off-screen content is included, not just the visible
 * viewport.
 */

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Browsers cap canvas dimensions (~16k px). Scale is clamped so
// scale × dimension never exceeds this, otherwise the capture comes back blank.
const MAX_CANVAS_DIMENSION = 16384;
const TARGET_SCALE = 2; // Higher quality when the content is small enough to afford it.

// 1px ≈ 0.264583mm at 96dpi — used to size the PDF page to the captured image.
const PX_TO_MM = 0.264583;

/** True when a computed background-color is fully transparent (or unset). */
function isTransparent(color: string): boolean {
  return !color || color === 'transparent' || /rgba\(\s*0\s*,\s*0\s*,\s*0\s*,\s*0\s*\)/.test(color);
}

/**
 * Resolve the background colour actually rendered behind the element, by
 * walking up until a non-transparent background is found.
 *
 * The capture must not hardcode white: in dark mode that puts dark cards and
 * light-grey axis labels on a white page, leaving the labels unreadable. Using
 * the live computed colour makes the export match what's on screen in either
 * theme (including `system`).
 */
function resolveBackgroundColor(element: HTMLElement): string {
  let node: HTMLElement | null = element;
  while (node) {
    const bg = window.getComputedStyle(node).backgroundColor;
    if (!isTransparent(bg)) return bg;
    node = node.parentElement;
  }
  const bodyBg = window.getComputedStyle(document.body).backgroundColor;
  return isTransparent(bodyBg) ? '#ffffff' : bodyBg;
}

async function captureElementCanvas(elementId: string): Promise<HTMLCanvasElement | null> {
  const element = document.getElementById(elementId);
  if (!element) {
    console.error(`Element with id "${elementId}" not found`);
    return null;
  }

  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

  let fullWidth = Math.max(element.scrollWidth, element.offsetWidth, element.clientWidth);
  let fullHeight = Math.max(element.scrollHeight, element.offsetHeight, element.clientHeight);
  element.querySelectorAll('*').forEach((node) => {
    if (node instanceof HTMLElement) {
      fullWidth = Math.max(fullWidth, node.scrollWidth);
      fullHeight = Math.max(fullHeight, node.scrollHeight);
    }
  });

  // Clamp scale so scale × dimension stays under the browser canvas ceiling.
  const maxScaleForWidth = MAX_CANVAS_DIMENSION / fullWidth;
  const maxScaleForHeight = MAX_CANVAS_DIMENSION / fullHeight;
  const scale = Math.max(1, Math.min(TARGET_SCALE, maxScaleForWidth, maxScaleForHeight));

  return html2canvas(element, {
    backgroundColor: resolveBackgroundColor(element),
    scale,
    logging: false,
    useCORS: true,
    width: fullWidth,
    height: fullHeight,
    windowWidth: fullWidth,
    windowHeight: fullHeight,
    scrollX: 0,
    scrollY: 0,
    onclone: (_clonedDoc, clonedElement) => {
      // Un-clip every scroll container so off-screen columns/rows render, and
      // grow the clone to its full content size.
      clonedElement.style.overflow = 'visible';
      clonedElement.style.width = `${fullWidth}px`;
      clonedElement.style.height = `${fullHeight}px`;

      clonedElement.querySelectorAll<HTMLElement>('*').forEach((node) => {
        const style = window.getComputedStyle(node);

        // Sticky headers / pinned columns would otherwise land at the wrong
        // offset (or overlap content). Pin them in normal flow instead.
        if (style.position === 'sticky') {
          node.style.position = 'static';
        }

        // Overflow clipping hides content beyond the viewport — open it up.
        if (
          style.overflow === 'hidden' ||
          style.overflowX === 'hidden' ||
          style.overflowY === 'hidden' ||
          style.overflowX === 'auto' ||
          style.overflowY === 'auto' ||
          style.overflowX === 'scroll' ||
          style.overflowY === 'scroll'
        ) {
          node.style.overflow = 'visible';
        }

        // Truncated labels (Tailwind `truncate` = ellipsis + nowrap) should
        // wrap so the full text is captured.
        if (style.textOverflow === 'ellipsis' || style.whiteSpace === 'nowrap') {
          node.style.textOverflow = 'clip';
          node.style.whiteSpace = 'normal';
          node.style.overflow = 'visible';
        }
      });
    },
  });
}

/** Capture an element by id and download it as a PNG. */
export async function exportElementToPNG(elementId: string, filename = 'export.png') {
  const canvas = await captureElementCanvas(elementId);
  if (!canvas) return;

  canvas.toBlob((blob) => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  });
}

/** Capture an element by id and download it as a PDF sized to the content. */
export async function exportElementToPDF(elementId: string, filename = 'export.pdf') {
  const canvas = await captureElementCanvas(elementId);
  if (!canvas) return;

  const imgData = canvas.toDataURL('image/png');
  const imgWidth = canvas.width;
  const imgHeight = canvas.height;

  // Fit the PDF page to the captured content instead of squeezing wide content
  // onto a fixed A4 (which shrinks text to an unreadable size). The page mirrors
  // the image's aspect ratio so text stays crisp regardless of dimensions.
  const pdfWidth = imgWidth * PX_TO_MM;
  const pdfHeight = imgHeight * PX_TO_MM;

  const pdf = new jsPDF({
    orientation: pdfWidth >= pdfHeight ? 'landscape' : 'portrait',
    unit: 'mm',
    format: [pdfWidth, pdfHeight],
  });

  // Fill the page edge-to-edge with the capture.
  pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
  pdf.save(filename);
}
